import format from 'date-fns/format';
import React, { useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import {
  MailSubscriptionJoin,
  getConstants,
  getModel,
  getSettings,
  setShowWidgetModal,
} from '@/entities/mailSubscription';

import type { TFormFieldsMailSubscription } from '@/shared/api/mailSubscription/types';
import { ButtonSecond } from '@/shared/components/Button';
import { TError } from '@/shared/components/ErrorMessage/types';
import { FORMAT_DATE_TIME } from '@/shared/constants';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/appRedux';
import { TPortalModalComponent } from '@/shared/services/modal/type';

import { BirthdayField } from './blocks/BirthdayField';
import { Footer } from './blocks/Footer';
import { Header } from './blocks/Header';
import { Input } from './blocks/Input';
import { MobileOptIn } from './blocks/MobileOptIn';
import { Roles } from './blocks/Roles';
import { Body, Content, RequiredFields, RequiredInfo, Wrapper } from './parts';

const MailSubscriptionModal: TPortalModalComponent = ({ close }) => {
  const model = useAppSelector(getModel);

  const settings = useAppSelector(getSettings);

  const constants = useAppSelector(getConstants);

  const form = useForm<TFormFieldsMailSubscription>({
    mode: 'onChange',
    shouldUnregister: true,
    defaultValues: {
      FirstName: model?.FirstName || undefined,
      LastName: model?.LastName || undefined,
      EmailAddress: model?.EmailAddress || undefined,
      MobileNumber: model?.MobileNumber || undefined,
      MobileOptIn: !!model?.MobileOptIn,
      Role: model?.Role,
      Birthday: model?.Birthday || undefined,
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setFocus,
  } = form;

  const { formatMessage } = useIntl();

  const messages = {
    firstName: formatMessage({ id: 'FirstNameLabel' }),
    lastName: formatMessage({ id: 'LastNameLabel' }),
    email: formatMessage({ id: 'EmailAddressLabel' }),
    phone: formatMessage({ id: 'MobileNumberLabel' }),
    birthday: formatMessage({ id: 'BirthdayLabel' }),
    birthdayPlaceholder: formatMessage({ id: 'BirthdayPlaceholder' }),

    emailPlaceholder: formatMessage({ id: 'EmailPlaceholder' }),
    phonePlaceholder: formatMessage({ id: 'MobileNumberPlaceholder' }),
    join: formatMessage({ id: 'Join' }),
    joined: formatMessage({ id: 'Joined' }),
    mobileOptIn: formatMessage({ id: 'MobileOptInLabel' }),
    headerTitle: formatMessage({ id: 'Title' }),
    headerSubTitle: formatMessage({ id: 'SubTitle' }),
    rolesTitle: formatMessage({ id: 'RolesTitle' }),
    requiredFields: formatMessage({ id: 'RequiredFields' }),
  };

  const dispatch = useAppDispatch<MailSubscriptionAppDispatch>();
  const handleJoin: SubmitHandler<Partial<TFormFieldsMailSubscription>> = (data) => {
    const idReCaptcha = constants?.captchaId || 'g-recaptcha-response';
    const reCaptcha = document?.getElementById(idReCaptcha) as HTMLInputElement | null;

    const postData = {
      FirstName: data.FirstName || '',
      LastName: data.LastName || '',
      EmailAddress: data.EmailAddress || '',
      MobileNumber: data.MobileNumber || '',
      MobileOptIn: !!data.MobileOptIn,
      Birthday: data.Birthday ? format(new Date(data.Birthday), FORMAT_DATE_TIME.MMdd) : null,
      Role: data.Role || null,
      UtmMedium: null,
      UtmSource: null,
      UtmCampaign: null,
      ...(reCaptcha &&
        reCaptcha?.name && {
          [reCaptcha.name]: reCaptcha?.value,
        }),
    };

    const gaEvent = (): void => {
      if (window?.gaGtag) {
        window.gaGtag.acquisitionFormEvent();
      }
    };
    const callback = (): void => {
      close();
      gaEvent();
    };
    dispatch(MailSubscriptionJoin(postData, callback));
  };

  // eslint-disable-next-line no-console
  const handleError = (errs: unknown): void => console.log('Error Join: ', errs);
  const regExpEmail = /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/;
  const regExpPhone = /^(?:(?:[+]\d){0,1}(?:\d[()\s-]*){10})$/;

  const handleClose = (): void => {
    dispatch(setShowWidgetModal(false));
    close();
  };

  const isMobileOpt = watch('MobileOptIn');

  useEffect(() => {
    setFocus('FirstName');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Wrapper className="Modal">
      <Header close={handleClose} />
      <Body>
        <RequiredInfo>
          <RequiredFields />
          <span
            dangerouslySetInnerHTML={{
              __html: messages.requiredFields,
            }}
          />
        </RequiredInfo>
        <Content>
          <div className="r-flex r-flex-row r-gap-4">
            <Input
              label={messages.firstName}
              id="FirstName"
              externalRules={{ required: false }}
              register={register}
              placeholder={messages.firstName}
              error={errors.FirstName as TError}
              autoComplete="given-name"
            />
            <Input
              label={messages.lastName}
              id="LastName"
              externalRules={{ required: false }}
              register={register}
              placeholder={messages.lastName}
              error={errors.LastName as TError}
              autoComplete="family-name"
            />
          </div>

          <Input
            label={messages.email}
            id="EmailAddress"
            externalRules={{
              required: {
                value: true,
                message: {
                  idIntl: 'IsRequiredMessage',
                  values: {
                    0: messages.email,
                  },
                },
              },
              pattern: {
                value: regExpEmail,
                message: {
                  idIntl: 'InvalidEmailAddress',
                  values: {
                    0: messages.email,
                  },
                },
              },
            }}
            register={register}
            placeholder={messages.emailPlaceholder}
            error={errors.EmailAddress as TError}
            autoComplete="email"
          />
          <div>
            <div className="r-flex r-flex-row r-gap-4">
              <Input
                className="r-w-full"
                label={messages.phone}
                id="MobileNumber"
                externalRules={{
                  required: {
                    value: !!isMobileOpt,
                    message: {
                      idIntl: 'IsRequiredMessage',
                      values: {
                        0: messages.phone,
                      },
                    },
                  },
                  pattern: {
                    value: regExpPhone,
                    message: {
                      idIntl: 'InvalidMobileNumber',
                      values: {
                        0: messages.phone,
                      },
                    },
                  },
                }}
                register={register}
                placeholder={messages.phonePlaceholder}
                error={errors.MobileNumber as TError}
                autoComplete="tel"
              />
              <FormProvider {...form}>
                <BirthdayField />
              </FormProvider>
            </div>
            <MobileOptIn register={register} />
          </div>

          <div className="r-flex r-flex-row r-gap-4">
            {constants?.roles && constants?.roles.length > 1 && (
              <Roles roles={constants.roles} register={register} error={errors.Role as TError} />
            )}
            <ButtonSecond
              isDark
              className="r-my-auto r-w-full r-p-0"
              click={handleSubmit(handleJoin, handleError)}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: messages.join,
                }}
              />
            </ButtonSecond>
          </div>
        </Content>
      </Body>

      <Footer smsTermsContent={settings?.SmsTermsContent} />
    </Wrapper>
  );
};

export default MailSubscriptionModal;
